import { useMemo, useRef } from 'react';

import type { GetPromptResponse } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import type { PromptCard } from '@atlassian/rovo-conversation-actions/prompt-cards';

import { mapApiPromptsToCards } from '../../common/utils/mapApiPromptsToCards';
import { sortPrompts } from '../../common/utils/sort-prompts';

/**
 * Hook that maintains stable ordering of prompts when favorite status changes.
 *
 * On initial load or when new prompts are added, prompts are sorted according to:
 * 1. Favorited prompts first (across all categories)
 * 2. Then non-favorited prompts in category order:
 *    a. User-generated prompts (non-system prompts)
 *    b. Priority system prompts (Analyze customer feedback, Brainstorm ideas, Send Slack message)
 *    c. System prompts with appIds (connected to 3P apps)
 *    d. System prompts with skill tags (invoking Rovo skills)
 *    e. Other system prompts
 *
 * Within each category, prompts are sorted by createdAt descending (newest first).
 *
 * When only the favorite status changes (e.g., user clicks favorite), the order is preserved
 * to prevent the list from jumping around.
 *
 * @param prompts - Raw prompts from the API response
 * @returns Prompt cards in stable order
 */
export function useStablePromptOrder(prompts: GetPromptResponse[]): PromptCard[] {
	const promptOrderRef = useRef<string[]>([]);

	return useMemo(() => {
		// On initial load or when new prompts are added, sort using the comprehensive sorting logic
		const currentIds = prompts.map((p) => p.id);
		const hasNewPrompts =
			promptOrderRef.current.length === 0 ||
			currentIds.some((id) => !promptOrderRef.current.includes(id));

		if (hasNewPrompts) {
			const sorted = sortPrompts(prompts);
			promptOrderRef.current = sorted.map((p) => p.id);
			return mapApiPromptsToCards(sorted);
		}

		// Use the stored order to maintain stability when favorite status changes
		const promptMap = new Map(prompts.map((p) => [p.id, p]));
		const orderedPrompts = promptOrderRef.current
			.filter((id) => promptMap.has(id))
			.map((id) => promptMap.get(id)!);

		return mapApiPromptsToCards(orderedPrompts);
	}, [prompts]);
}
